import { useEffect } from "react";

import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";

import { useCompany } from "@/hooks/util/useCompany";
import {
  loadClientDashboard,
  loadClientTotalDashboard,
  selectClientDashboardState
} from "@/store/features/clientDashboard/clientDashboard";
import {
  loadEmailStatus,
  loadWhatsappStatus,
  selectConnectionsSettings
} from "@/store/features/clientSettings/connections/connections";

interface ClientDashboardProps {
  label: string;
  after: Date | null | string;
  before: Date | null | string;
}

export const useClientDashboard = ({ before, after }: ClientDashboardProps) => {
  const { activeClientId, activeCompanyId } = useCompany();

  const dispatch = useDispatch();

  const { emailStatus, whatsappStatus } = useSelector(
    selectConnectionsSettings
  );

  const dateFormat = "yyyy-MM-dd";

  useEffect(() => {
    if (activeClientId || activeCompanyId) {
      dispatch(loadClientTotalDashboard(activeClientId as string));
      dispatch(loadEmailStatus(activeClientId!!, "gmail"));
      dispatch(loadWhatsappStatus());
    }
  }, [dispatch, activeCompanyId, activeClientId]);

  useEffect(() => {
    // Only make the API call if we have valid dates and an active client/company
    if ((activeClientId || activeCompanyId) && before && after) {
      try {
        // Convert dates to Date objects if they're strings
        const beforeDate = typeof before === 'string' ? new Date(before) : before;
        const afterDate = typeof after === 'string' ? new Date(after) : after;
        
        const formattedBefore = format(beforeDate, dateFormat);
        const formattedAfter = format(afterDate, dateFormat);
        
        dispatch(
          loadClientDashboard(activeClientId as string, {
            before: formattedBefore,
            after: formattedAfter
          })
        );
      } catch (error) {
        console.error("Error formatting dates in useClientDashboard:", error);
      }
    }
  }, [dispatch, activeCompanyId, before, after, activeClientId]);

  const {
    dashboard,
    isDashboardLoading,
    totalDashboard,
    isTotalDashboardLoading
  } = useSelector(selectClientDashboardState);

  return {
    dashboard,
    isDashboardLoading,
    totalDashboard,
    isTotalDashboardLoading,
    emailStatus,
    whatsappStatus
  };
};
